import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';

const CustomPromptManager = ({ role, onAddCustomPrompt }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [promptName, setPromptName] = useState('');
  const [promptContent, setPromptContent] = useState('');
  const [customPrompts, setCustomPrompts] = useState([]);

  // Load custom prompts for specific role from localStorage on component mount
  // and when role changes
  useEffect(() => {
    const savedPrompts = localStorage.getItem(`customPrompts_${role}`);
    if (savedPrompts) {
      setCustomPrompts(JSON.parse(savedPrompts));
    } else {
      setCustomPrompts([]); // Reset prompts when switching to a role with no saved prompts
    }
  }, [role]); // Add role as dependency to reload when role changes

  // Save custom prompts to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(`customPrompts_${role}`, JSON.stringify(customPrompts));
  }, [customPrompts, role]);

  const handleSubmit = () => {
    if (promptName && promptContent) {
      const newPrompt = {
        name: promptName,
        content: promptContent,
        role: role // Store the role with the prompt
      };

      setCustomPrompts(prev => [...prev, newPrompt]);
      onAddCustomPrompt(newPrompt);
      
      // Reset form
      setPromptName('');
      setPromptContent('');
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-6 py-2 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
      >
        <Plus size={16} />
        <span>Custom Prompt</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                Create Custom Prompt for {role}
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Prompt Name
                </label>
                <input
                  type="text"
                  value={promptName}
                  onChange={(e) => setPromptName(e.target.value)}
                  className="w-full p-2 border text-gray-700 border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter prompt name"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Prompt Content
                </label>
                <textarea
                  value={promptContent}
                  onChange={(e) => setPromptContent(e.target.value)}
                  className="w-full p-2 border text-gray-700 border-gray-300 rounded-md h-32 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter prompt content"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 p-4 border-t">
              <button
                onClick={() => setIsOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Create Prompt
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomPromptManager;